<template>
  <div>
    <div id="schedulercontainer">
      <v-progress-linear v-if="loading" rounded indeterminate height="1" absolute></v-progress-linear>
    </div>
    <div class="hidden" style="display: none">
      <div ref="popup">
        <div class="text-center" style="display: flex; width: 100%" v-if="tooltipLoading">
          <v-spacer />
          <v-progress-circular class="py-5 my-5" v-if="true" indeterminate></v-progress-circular>
          <v-spacer />
        </div>
        <v-app style="font-family: Nunito Sans;">
          <JobListItem v-if="!tooltipLoading" :job-item="tooltipData" noclick previewMode showStatus>
          </JobListItem>
        </v-app>
      </div>
    </div>
  </div>
</template>


<script>
import clientAPI from "@/api/ClientAPIMixin.js";
import JobListItem from "@/components/ListItems/JobListItem/JobListItem.vue";
import SchedulerDragMixin from "@/views/planning/SchedulerDragMixin.js"; //methods to handle drag and drop from external sources into scheduler
import SchedulerSignalRMixin from "@/views/planning/SchedulerSignalRMixin.js"; //methods to handle signalr events in relation to the scheduler
import { SchedulerPro } from "@bryntum/schedulerpro";
import SchedulerRenderMixin from "./SchedulerRenderMixin"; //methods to render customized parts of the scheduler 
import SchedulerUiLogicMixin from "./SchedulerUiLogicMixin"; //methods to alter scheduler ui based on filters, settings etc.

export default {
  name: "Scheduler",
  props: ["routeData"],
  components: { JobListItem },
  mixins: [
    SchedulerDragMixin,
    SchedulerSignalRMixin,
    SchedulerRenderMixin,
    SchedulerUiLogicMixin,
  ],

  data() {
    return {
      tooltipData: null,
      tooltipLoading: false,
      loading: false,
      startDate: new Date(new Date().setHours(0, 0, 0, 0)),
      endDate: null,
      scheduler: null,

      todayTimeRange: {
        id: 1,
        name: 'Today',
        startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        duration: 1,
        durationUnit: 'day',
        style: `background-color: rgba(181, 24, 243, 0.03);`
      },
      resourceList: [],
      filteredResourceList: [],
      assignmentList: [],
      eventList: [],
    };
  },

  created() {
    if (!this.scheduler) {
      this.initScheduler();
    }
    else {
      this.getSchedulerStartAndEnd(this.startDate);
      this.getSchedulerElements();
    }
  },

  beforeDestroy() {
    if (this.dragHelper) {
      this.dragHelper.destroy();
    }
  },

  methods: {
    clickEvent(args) {
      console.log("CLICKED A CELL", args, "UserId", args.cellSelector.id)
      this.$emit("update:routeData",{ userId: args.cellSelector.id, date: this.startDate})
    },
    loadTooltip(jobId) {
      if (jobId) {
        this.tooltipLoading = true;
        clientAPI.methods.getJobById(jobId, true).then((data) => {
          this.tooltipData = data;
        })
          .finally(() => {
            this.tooltipLoading = false;
          });
      }
      else {
        this.tooltipLoading = false;
        this.tooltipData = null;
      }
    }, 

    async initScheduler() {
      this.setCalendarDate(this.startDate);
      this.getSchedulerStartAndEnd(this.startDate);
      await this.getSchedulerElements();
      var callClickEvent = (args) => { this.clickEvent(args) }
      var setCalendarDateTarget = (val) => { this.setCalendarDate(val) }
      var callChangeViewPreset = (val) => { this.setPlannerScope(val) }
      var callOpenJob = (id) => { this.openJob(id) }
      var callEventDialog = (e) => { return this.openEventDialog(e) };
      var callEventRenderer = (obj) => { return this.renderEvent(obj); };
      var callGroupRenderer = (obj) => { return this.renderGroup(obj); };
      var callWeekLabelRenderer = (obj) => { return this.renderWeekLabel(obj); };
      var callEventContextMenuExtraItemsRenderer = (obj) => { return this.renderEventContextMenuExtraItems(obj); };
      var callChangeAppointmentTimes = (obj) => { return this.changeEventTimesInBackend(obj.eventRecord) };
      var callEventMoveDrop = (obj) => { return this.eventMoveDrop(obj) };

      const initialMode = this.getInitialMode();
      var initialStart = this.getInitialStartDate();
      var initialEnd = this.getInitialEndDate();
      var initialGroupBy = this.getInitialGroupBy();

      const todayTimeRange = this.todayTimeRange;
      const timeRanges = (initialMode != 'QDay') ? [todayTimeRange] : [];

      const scheduler = new SchedulerPro({
        appendTo: "schedulercontainer",
        startDate: initialStart,
        endDate: initialEnd,

        project: {
          eventsData: this.eventList,
          resourcesData: this.filteredResourceList,
          assignmentsData: this.assignmentList,
        },
        presets: [
          {
            id: 'QDay',
            base: "hourAndDay",
            timeResolution: { unit: 'minute', increment: 15 },
            headers: [
              { align: 'start', unit: 'day', dateFormat: 'dddd D. MMMM YYYY' },  //day header
              { unit: 'hour', dateFormat: 'H:mm' }  //time of day header
            ],
          },
          {
            id: 'QWeek',
            base: 'dayAndWeek',
            timeResolution: { unit: 'day', increment: 1 },
            headers: [
              { align: 'start', unit: 'week', renderer: callWeekLabelRenderer }, //week header
              { unit: 'day', dateFormat: 'ddd D. MMM' }, //day header
            ],
          },
          {
            id: 'QMonth',
            base: 'dayAndMonth',
            mainUnit: 'month',
            timeResolution: { unit: 'day', increment: 1 },
            headers: [
              { align: 'start', unit: 'month', dateFormat: 'MMMM Y' }, //month header
              //{ align: 'start', unit: 'week', renderer: (obj) => { return callWeekLabelRenderer(obj); } }, //week header, WARNING: breaks start end times by going outside month
              { unit: 'day', dateFormat: 'ddd D.' }, //day header
            ],
          },
        ],
        viewPreset: initialMode,
        timeRanges: timeRanges,
        weekStartDay: 1,
        zoomOnTimeAxisDoubleClick: false, //disable zoom on header double click
        zoomOnMouseWheel: false, //disable zoom scroll
        createEventOnDblClick: false, //disable new event on scheduler double click
        allowCreate: false, //disables creating dependencies
        enableDeleteKey: false,
        rowHeight: 50,
        barMargin: 5,
        eventColor: "blue",
        passStartEndParameters: true,
        fillTicks: initialMode != 'QDay', //fill column width on all view presets except QDay
        //snap: true,
        features: {
          timeRanges: { showCurrentTimeLine: initialMode == 'QDay', showHeaderElements: true }, //only add today range on views other than QDay
          timeAxisHeaderMenu: false, //disables right click menu on time axis header
          dependencies: false, //disables dependency lines and anchors on appointments
          scheduleMenu: false, //disables right click menu on empty space on planner
          eventCopyPaste: false, //disables copy paste appointment
          scheduleTooltip: false, //disables empty cell hover tooltip
          group: { field: initialGroupBy || 'type', disabled: !initialGroupBy, renderer: callGroupRenderer }, //resource grouping config based on external
          eventMenu: { //right click on appointments
            items: {
              editEvent: false, //hides default menu item
              unassignEvent: false, //hides default menu item
              splitEvent: false, //hides default menu item
              deleteEvent: false, //hides default menu item
            },
            processItems: callEventContextMenuExtraItemsRenderer, //generates menu items based on the event you right clicked
          },
          eventTooltip: {
            hoverDelay: 500,
            adopt: this.$refs.popup,
            onBeforeShow: ({source: tooltip}) => this.loadTooltip(tooltip.eventRecord._data.jobId)
          },
          /*
          calendarHighlight: {
            calendar: 'event',
            // This method should return the available resources for one or more events
            collectAvailableResources({ eventRecords }) {
              return getAvailableResources(eventRecords[0]);
            }
          },
          */
          /*
           eventDrag: {
             constrainDragToResource: false,
             // This method is used to validate drag drop operations
             validatorFn({ eventRecords, startDate, endDate }) {
               const
                 task = eventRecords[0],
                 { calendar } = task,
                 valid = (!calendar || calendar.isWorkingTime(startDate, endDate, true)),
                 message = valid ? '' : 'No available slot';
 
               return {
                 valid,
                 message: (valid ? '' : '<i class="b-icon b-fa-exclamation-triangle"></i>') + message
               };
             },
             // Affect snapping on drag, making events snap to the vertical center of each resource
             snapToPosition({ resourceRecord, eventRecord, snapTo }) {
               if (scheduler.snap) {
                 const row = scheduler.getRowFor(resourceRecord);
                 if (row) {
                   const
                     eventElement = scheduler.getElementFromEventRecord(eventRecord),
                     eventHeight = eventElement.offsetHeight,
                     rowCenter = row.top + row.height / 2,
                     distanceFromCenter = Math.abs(snapTo.y + eventHeight / 2 - rowCenter);
 
                   if (distanceFromCenter < 15) {
                     snapTo.y = rowCenter - eventHeight / 2;
                   }
                 }
               }
             }
           },
           */
        },

        columns: [
          {
            text: "Users",
            field: "name",
            width: 165,
            type: "resourceInfo",
            hidable: true,
            enableCellContextMenu: false,
            enableHeaderContextMenu: false,
            cellMenuItems: {
              customItem: {
                icon: 'b-fa-up-right-and-down-left-from-center',
                text: 'Their events',
                menu: [
                  { icon: 'b-fa-list', text: 'as list' },
                  { icon: 'b-fa-calendar-days', text: 'as schedule' },
                ]
              },
              customItem2: { icon: 'b-fa-eye-slash', text: 'Hide' },
            }
          },
        ],
        onCellClick: callClickEvent,
        onEventClick: callEventDialog, //on appointment click
        eventRenderer: callEventRenderer,
        onEventResizeEnd: callChangeAppointmentTimes,
        onBeforeEventEdit: () => false, //overrides edit event behavior, prevents default dialog by returning false.
        onDataChange: (e) => console.log('onDataChange', e), //on any data change, has propperties for what type of action was done (ie delete or update)
        onDragCreateEnd: callEventDialog,
        onEventDrop: callEventMoveDrop,
        onEventAutoCreated: (e) => console.log('onEventAutoCreated', e),
        onEventDblClick(e) { //on appointment double click

          if (e.eventRecord.jobId) {
            callOpenJob(e.eventRecord.jobId);
          }
        },
        onEventDrag: (e) => console.log('onEventDrag', e),
        onEventKeyDowne: (e) => console.log('onEventKeyDowne', e),
        onTimeAxisHeaderClick(e) { //On header click (not today indicator)

          if (e.event.target.classList.value.includes("b-sch-dayheadercell-")) {
            setCalendarDateTarget(new Date(e.startDate));
            callChangeViewPreset('QDay');
          }
        },
        onTimeRangeHeaderClick() { //On header today indicator click
          setCalendarDateTarget(new Date());
          callChangeViewPreset('QDay');
        },
        onCellDblClick: () => false, //disables the edit mode on resources
        test: (e) => console.log('test', e),
      });

      this.scheduler = scheduler;
      this.initializeDrag();
    },

    async checkIdIsFrontendOnly(id) {
      var result = (await this.$store.dispatch("plannerstore/checkIdIsFrontendOnly", id));
      return result;
    },

    openEventDialog(e) {
      this.$emit('eventDialog', e);
    },

    toggleEventLoading(event, val) {
      event.loading = val;
      event.setInactive(val);
    },

    async deleteAppointmentSafe(obj) {
      this.toggleEventLoading(obj.eventRecord, true)
      var id = obj.eventRecord.id;
      var existsInBE = !(await this.checkIdIsFrontendOnly(id));
      if (existsInBE) {
        await clientAPI.methods.deleteAppointment(id)
          .then((/*response*/) => {
            obj.eventRecord.remove();
          })
          .catch((e) => {
            console.error(e);
            this.toggleEventLoading(obj.eventRecord, false);
          });
      }
      else {
        obj.eventRecord.remove();
      }
    },

    async getSchedulerElements() {
      this.loading = true;
      this.resourceList = await this.getResourceList();
      this.filteredResourceList = this.filterResourceList();
      var { events, assignments } = await clientAPI.methods.getSchedulerDtoForDateRange(this.startDate, this.endDate);
      this.eventList = events;
      this.assignmentList = assignments;

      if (this.scheduler) {
        this.scheduler.resources = this.filteredResourceList;
        this.scheduler.events = this.eventList;
        this.scheduler.assignments = this.assignmentList;
      }
      this.loading = false;
    },

    async addOrUpdateEventInStoreById(id, updatedElements) {
      var vntStore = this.scheduler._eventStore;
      var assStore = this.scheduler._assignmentStore;

      if (!updatedElements) { //if no new values provided get fresh ones
        updatedElements = await clientAPI.methods.getAppointmentAsSchedulerDtos(id);
      }

      var e = updatedElements.events;
      var assArr = updatedElements.assignments;
      var record = vntStore.getById(id);

      if (record) { //if record exist assign the new values to it
        //assign event values
        record.name = e.name;
        record.appointmentType = e.appointmentType;
        record.startDate = e.startDate;
        record.endDate = e.endDate;
        record.jobId = e.jobId;
        record.jobTypeId = e.jobTypeId;
        record.jobTypeName = e.jobTypeName;
        record.jobStatusId = e.jobStatusId;
        record.jobStatusName = e.jobStatusName;
        record.address = e.address;
        //assign event to resources
        if (assArr && assArr.length) {
          assStore.assignEventToResource(record, assArr.map((ass) => ass.resourceId), null, true);
        }
      }
      else {//if it didnt exist just add it normally
        await vntStore.add(e, true);
        if (assArr && assArr.length) {
          await assStore.add(assArr, true);
        }
      }
      assStore.commit();
      vntStore.commit();
    },

    removeEventInStoreById(id) {
      var vntStore = this.scheduler._eventStore;
      //var assStore = this.scheduler._assignmentStore;
      vntStore.suspendEvents(false);
      vntStore.remove(id);
      vntStore.resumeEvents();
    },

    openJob(jobId, newTab) {
      if (newTab) {
        let newPageRoute = this.$router.resolve({ name: "ViewJob", params: { filter: "my", jobId: jobId } });
        window.open(newPageRoute.href, "_blank");
      }
      else {
        this.$router.push({ name: "ViewJob", params: { filter: "my", jobId: jobId } });
      }
    },

    async changeEventTimesInBackend(event) {
      //TODO: show editor
      this.toggleEventLoading(event, true)
      await this.changeEventTimes(event);
      this.toggleEventLoading(event, false)
    },

    async eventMoveDrop(context) {
      const
        event = context.eventRecords[0],
        resource = context.targetResourceRecord;
      //console.log(/*context,*/ event?.name, resource?.name);
      this.toggleEventLoading(event, true)
      //await this.changeEventTimes(event);
      var appointment = {
        Id: event.id,
        Subject: event.name,
        AppointmentDate: event.startDate,
        StartTime: event.startDate,
        EndTime: event.endDate,
        JobId: event.jobId,
        Participants: [{ Id: resource.id, Name: resource.name }]
      };
      appointment = await clientAPI.methods.changeAppointment(appointment);
      this.toggleEventLoading(event, false);
      this.addOrUpdateEventInStoreById(appointment.Id);
      this.$emit("change")
    },
  },

  computed: {},

  watch: {},
};
</script>


<style lang='scss'>
@import '~@bryntum/schedulerpro/schedulerpro.stockholm.css';


#schedulercontainer {
  width: 100%;
  height: calc(100vh - 142px);
}

.b-grid-header .b-sch-timerange {
  background-color: rgb(169, 78, 244) !important;
}

.b-timeline-subgrid .b-sch-line {
  border-color: rgb(169, 78, 244) !important;
}

.b-sch-event {
  border-radius: 4px;
}

.b-sch-header-text {
  pointer-events: none;
}

.b-group-title div {
  //re aligns group header title
  margin-top: 0px !important;
  margin-left: 0px !important;
  opacity: 1 !important;
}

.eventloading {
  background-image: repeating-linear-gradient(120deg, #ffffff00 10px, #eeeeee89, #eeeeeeaa 150px) !important;
  /* Expanded the size of the image to prevent visual blips when animation loop repeats */
  background-size: 6000px;
  animation: animatedBackground 70s linear infinite;
}

@keyframes animatedBackground {
  from {
    background-position: 100% 0;
  }

  to {
    background-position: -100% 0;
  }
}

.b-grid-row.b-group-row {
  background-color: #f0f0f0 !important;
}

.b-grid-row:nth-child(odd) {
  background-color: #003cff04;
}

/* GRID LINE COLORING ---------------- */
.b-grid-row {
  border-bottom: 1px solid #e4e4e4;
}

.b-column-line {
  border: 1px solid #f2f2f2;
}

/*------------------------------------ */

.test123 {
  background-color: rgba(137, 158, 254, 0.048);
}

.dragproxy {
  background-color: #53b551;
  color: white;
  padding: 12px;
}

.dragproxyOverInvalidTarget {
  opacity: 0.8;
  background-color: rgb(98, 98, 98);
  //color: black;
}

/*
.b-gridbase:not(.b-moving-splitter) .b-grid-subgrid:not(.b-hide-row-hover) .b-grid-row:not(.b-group-row).b-hover,
*/
.b-hover {
  background-color: none !important;
}

.b-grid-row:not(.b-group-row):hover {
  border: 1px solid rgba(0, 0, 255, 0.221);
  //background-color: rgba(255, 191, 0, 0.055) !important;
}

.b-drag-original {
  opacity: 0;
}

.dragHoverRow {
  border: 2px solid rgb(136, 0, 255) !important;
  // border: 2px dashed var(--v-tertiary-base) !important;
}
</style>